import React from 'react';
import { theme } from '../../design/theme';

interface DataTableProps<T> {
  columns: {
    header: string;
    accessor: keyof T;
    render?: (value: any, row: T) => React.ReactNode;
    width?: string;
  }[];
  data: T[];
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
}

export const DataTable = <T extends object>({ 
  columns, 
  data, 
  isLoading,
  onRowClick,
  emptyMessage = 'No data available'
}: DataTableProps<T>) => {
  if (isLoading) {
    return (
      <div style={{ ...theme.card, padding: theme.spacing.xl, textAlign: 'center' }}>
        <div style={{ ...theme.typography.body, color: theme.colors.neutral[500] }}>Loading data...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ ...theme.card, padding: theme.spacing.xl, textAlign: 'center' }}>
        <div style={{ ...theme.typography.body, color: theme.colors.neutral[500] }}>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      ...theme.card, 
      overflow: 'hidden',
      width: '100%'
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          textAlign: 'left'
        }}>
          <thead>
            <tr style={{ 
              backgroundColor: theme.colors.neutral[50],
              borderBottom: `1px solid ${theme.colors.neutral[200]}`
            }}>
              {columns.map((col, idx) => (
                <th key={idx} style={{ 
                  ...theme.typography.tableHeader,
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  color: theme.colors.neutral[600],
                  width: col.width
                }}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr 
                key={rowIdx} 
                onClick={() => onRowClick?.(row)}
                style={{ 
                  borderBottom: `1px solid ${theme.colors.neutral[100]}`,
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = theme.colors.neutral[50];
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                {columns.map((col, colIdx) => (
                  <td key={colIdx} style={{ 
                    ...theme.typography.table,
                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                    color: theme.colors.neutral[900]
                  }}>
                    {col.render ? col.render(row[col.accessor], row) : String(row[col.accessor])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
